// src/components/RiskSetup/templates/TemplateStartStep.component.tsx
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EditOutlined, MenuBookOutlined } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskTemplateCatalogEntry } from '@Types/riskTemplateCatalog.types';
import { StartOptionCard } from './StartOptionCard.component';

export type StartMethod = '' | 'template' | 'scratch';

interface Props {
	templates: RiskTemplateCatalogEntry[];
	startMethod: StartMethod;
	selectedTemplateId: string;
	onSelect: (method: StartMethod, templateId?: string) => void;
	onInfo: () => void;
}

export const TemplateStartStep = ({
	templates,
	startMethod,
	selectedTemplateId,
	onSelect,
	onInfo,
}: Props) => {
	const { t } = useGlobalization();
	return (
		<Box sx={{ width: '620px', maxWidth: '100%' }}>
			<Typography
				sx={{ fontSize: 18, fontWeight: 700, color: '#0d1520' }}>
				{t('How do you want to start?')}
			</Typography>
			<Typography sx={{ fontSize: 13, color: '#5b6472', mt: 0.5, mb: 2 }}>
				{t(
					'Start from an industry-standard template and adjust it, or build from scratch with your own methodology.',
				)}
			</Typography>
			<Stack spacing={1.25}>
				{templates.map(template => (
					<StartOptionCard
						key={template.id}
						selected={
							startMethod === 'template' &&
							selectedTemplateId === template.id
						}
						onSelect={() => onSelect('template', template.id)}
						icon={
							<MenuBookOutlined
								sx={{ fontSize: 20, color: '#2a7de1' }}
							/>
						}
						iconBg="#e8f1fd"
						eyebrow={t('TEMPLATE')}
						title={template.label}
						onInfo={onInfo}
						body={
							<Box>
								<Typography
									sx={{ fontSize: 12.5, color: '#5b6472' }}>
									{template.provenance}
								</Typography>
								<Typography
									sx={{
										fontSize: 12.5,
										color: '#3a4451',
										mt: 1,
									}}>
									{template.summary}
								</Typography>
								<Box
									sx={{
										mt: 1.25,
										p: 1.25,
										borderRadius: '4px',
										background: '#f2f7fd',
										fontSize: 12.5,
										color: '#1f4e79',
									}}>
									<b>{t('Why use this?')}</b>{' '}
									{t(
										'A defensible starting point built on what other utilities use. Adjust anything you like — you just won\u2019t be starting from a blank slate.',
									)}
								</Box>
							</Box>
						}
					/>
				))}
				<StartOptionCard
					selected={startMethod === 'scratch'}
					onSelect={() => onSelect('scratch', undefined)}
					icon={
						<EditOutlined sx={{ fontSize: 20, color: '#5b6472' }} />
					}
					iconBg="#eef1f4"
					title={t('Build from scratch')}
					body={
						<Typography sx={{ fontSize: 12.5, color: '#5b6472' }}>
							{t(
								'Choose your own components, scores, and weights. Recommended if you have a specific methodology or existing scoring system.',
							)}
						</Typography>
					}
				/>
			</Stack>
		</Box>
	);
};
